import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AddSpotModal from "./AddSpotModal";
import ClaimVerifyModal from "./ClaimVerifyModal";
import { API } from "../../../utils/api";
import { AUTH_TOKEN_TEXT } from "../../../Constants";
import { getLocalData, saveLocalData } from "../../../utils/LocalStorage";
import { loginUser } from "../../../Store/Reducers/AuthReducers";
import { isValidEmail, showToast } from "../../../utils/helper";
import { trackEvent } from "../../../utils/analytics";

/**
 * Progressive-save landing wizard.
 *
 * Step 1 — email gate
 *   POST /user/lead-check. exists:false → advance blank. exists:true →
 *   server also sent an OTP; open ClaimVerifyModal (step1-existing). On
 *   verify success we get accessToken + user + draftSpot; sign the user
 *   in and, if a draft exists, prefill AddSpotModal.
 *
 * Step 2 — spot details
 *   Next posts a multipart details-only payload to /spot/save-draft.
 *   Server upserts a draft spot and returns a fresh accessToken so the
 *   brand-new user is authenticated for step 3.
 *
 * Step 3 — availability + activate
 *   Add Spot posts JSON to /spot/activate; server attaches availability
 *   and flips status → active. If the user is still inactive (brand-new
 *   path), server sends the OTP + welcome-with-password emails and we
 *   open ClaimVerifyModal (step3-postsignup) before routing to
 *   /user/spots.
 */
const ClaimSpotModal = ({ show, onClose, onSuccess, city }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(
    (s) => s.auth && s.auth.isAuthenticated,
  );

  // Authed users skip the email gate entirely and land on step 2 directly.
  // Check Redux + the raw token so we don't flash the email modal while
  // Redux is still hydrating from the token on landing pages.
  const authedAtOpen = isAuthenticated || !!getLocalData(AUTH_TOKEN_TEXT);
  const [step, setStep] = useState(authedAtOpen ? "spot" : "email");
  const [lead, setLead] = useState({ leadName: "", email: "" });
  const [leadErrors, setLeadErrors] = useState({});
  const [checking, setChecking] = useState(false);

  // AddSpotModal prefill payload after a step-1 draft resume. When null
  // the form starts blank (new user or existing user with no draft).
  const [initialSpot, setInitialSpot] = useState(null);

  // OTP modal drives two flows. "step1-existing" verifies a returning
  // user before revealing their draft. "step3-postsignup" is the classic
  // signup-verify shown after /spot/activate for brand-new users.
  const [verifyMode, setVerifyMode] = useState(null);
  const [verifyEmail, setVerifyEmail] = useState("");

  // Already-authed user opened the wizard: pull their existing draft (if
  // any) so step 2 renders pre-filled. Fires once on mount when the user
  // was authed at open time (the unauthed → step-1-verify path prefills
  // via handleStep1Verified instead).
  useEffect(() => {
    if (!authedAtOpen) return;
    API.getUserSpots({ status: "draft", limit: 1 })
      .then((res) => {
        if (
          res &&
          res.type === "success" &&
          Array.isArray(res.data) &&
          res.data.length > 0
        ) {
          setInitialSpot(res.data[0]);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setStep("email");
    setLead({ leadName: "", email: "" });
    setLeadErrors({});
    setChecking(false);
    setInitialSpot(null);
    setVerifyMode(null);
    setVerifyEmail("");
    if (typeof onClose === "function") onClose();
  };

  const signIn = (accessToken, user) => {
    if (accessToken) saveLocalData(AUTH_TOKEN_TEXT, accessToken);
    if (user) {
      dispatch(loginUser({ user, accessToken, isAuthenticated: true }));
    }
  };

  const handleEmailNext = () => {
    if (checking) return;
    const email = lead.email.trim().toLowerCase();
    if (!email) {
      setLeadErrors({ email: "Please enter your email" });
      return;
    }
    if (!isValidEmail(email)) {
      setLeadErrors({ email: "Please enter a valid email" });
      return;
    }
    setLeadErrors({});
    setChecking(true);

    API.leadCheck({ email })
      .then((res) => {
        if (res && res.type === "success") {
          trackEvent("web_claim_email_submited", { city, email });
          if (res.exists) {
            // Server has sent the OTP already; show the verify modal.
            setVerifyEmail(email);
            setVerifyMode("step1-existing");
          } else {
            // Brand-new user — proceed as a lead. AddSpotModal starts blank.
            setInitialSpot(null);
            setStep("spot");
          }
          return;
        }
        const msg =
          (res && res.details && res.details[0] && res.details[0].message) ||
          (res && res.message) ||
          "Could not verify email. Please try again.";
        setLeadErrors({ email: msg });
      })
      .catch((err) =>
        setLeadErrors({
          email:
            (err && err.message) || "Could not verify email. Please try again.",
        }),
      )
      .finally(() => setChecking(false));
  };

  // Step-1 OTP verify success. Server returned accessToken + user + the
  // draft spot (may be null). Sign the user in, prefill if a draft exists,
  // then advance to step 2.
  const handleStep1Verified = (res) => {
    const accessToken = res && res.accessToken;
    const user = res && res.data && res.data.user;
    const draftSpot = res && res.data && res.data.draftSpot;
    signIn(accessToken, user);
    setInitialSpot(draftSpot || null);
    setVerifyMode(null);
    setStep("spot");
  };

  const handleStepAdvance = (fromTab) => {
    if (fromTab === "details") {
      trackEvent("web_claim_details_submited", { city });
    } else if (fromTab === "availability") {
      trackEvent("web_claim_spot_created", { city });
    }
  };

  const validateLead = () => {
    const errs = {};
    if (!lead.leadName.trim()) errs.leadName = "Please enter your name";
    setLeadErrors(errs);
    if (Object.keys(errs).length > 0) {
      showToast("error", errs.leadName);
      return false;
    }
    return true;
  };

  // Step 2 — persist the spot as a draft. Server returns updated spot
  // (with id + image URLs) + a fresh accessToken. Merge id + images back
  // into the local form via AddSpotModal's onDetailsSave contract.
  const handleDetailsSave = async (fd) => {
    // Attach lead fields only in the anonymous-lead path. Authenticated
    // users already have their name on their profile; /spot/save-draft
    // uses the auth header.
    if (!isAuthenticated) {
      fd.append("leadName", lead.leadName.trim());
      fd.append("email", lead.email.trim().toLowerCase());
    }
    try {
      const res = await API.saveDraftSpot(fd);
      if (res && res.type === "success") {
        const accessToken = res.accessToken;
        const user = res.data && res.data.user;
        signIn(accessToken, user);
        return { ok: true, spot: res.data && res.data.spot };
      }
      const details = res && res.details;
      const msg =
        (details && details[0] && details[0].message) ||
        (res && res.message) ||
        "Could not save your spot. Please try again.";
      showToast("error", msg);
      return { ok: false };
    } catch (err) {
      showToast(
        "error",
        (err && err.message) || "Could not save your spot. Please try again.",
      );
      return { ok: false };
    }
  };

  // Step 3 — attach availability + activate the draft. If the user is
  // still inactive, server sends OTP + welcome-with-password and reports
  // needsOtp:true, in which case we open the post-signup verify modal.
  const handleActivate = async (payload) => {
    try {
      const res = await API.activateSpot(payload);
      if (res && res.type === "success") {
        trackEvent("web_claim_success", { city });
        const needsOtp = res.data && res.data.needsOtp;
        const user = res.data && res.data.user;
        if (needsOtp) {
          const email = (user && user.email) || lead.email.trim().toLowerCase();
          setVerifyEmail(email);
          setVerifyMode("step3-postsignup");
        } else {
          showToast(
            "success",
            res.message || "Your spot is live!",
          );
          if (typeof onSuccess === "function") onSuccess(res);
          navigate("/user/spots");
        }
        return { ok: true };
      }
      trackEvent("web_claim_failed", { city, reason: "activate" });
      const details = res && res.details;
      const msg =
        (details && details[0] && details[0].message) ||
        (res && res.message) ||
        "Could not activate your spot. Please try again.";
      showToast("error", msg);
      return { ok: false };
    } catch (err) {
      trackEvent("web_claim_failed", { city, reason: "activate" });
      showToast(
        "error",
        (err && err.message) ||
          "Could not activate your spot. Please try again.",
      );
      return { ok: false };
    }
  };

  const handleStep3Verified = () => {
    setVerifyMode(null);
    if (typeof onSuccess === "function") onSuccess();
    handleClose();
    navigate("/user/spots");
  };

  const detailsHeader = (
    <div className="claim-lead-fields mb-3">
      <div className="claim-email-locked d-flex align-items-center justify-content-between mb-3 p-2 px-3 rounded bg-light">
        <span className="text-muted small">
          Signing up as <strong>{lead.email}</strong>
        </span>
        <button
          type="button"
          onClick={() => setStep("email")}
          style={{
            background: "transparent",
            border: 0,
            padding: 0,
            color: "#0d6efd",
            textDecoration: "underline",
            fontSize: "0.875rem",
            cursor: "pointer",
            boxShadow: "none",
            outline: "none",
          }}
        >
          Change
        </button>
      </div>
      <div className="row g-3">
        <div className="col-12">
          <Form.Group>
            <Form.Label className="fw-bold">Your Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Full name"
              value={lead.leadName}
              onChange={(e) =>
                setLead((l) => ({ ...l, leadName: e.target.value }))
              }
              isInvalid={!!leadErrors.leadName}
            />
            {leadErrors.leadName && (
              <Form.Control.Feedback type="invalid">
                {leadErrors.leadName}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </div>
      </div>
    </div>
  );

  // Wizard mode branches on the auth state at the time step 2 opens.
  // Anonymous lead → lead-capture header + validate name. Already
  // authenticated (via step-1 OTP verify) → no header, profile has name.
  const authedMode = !!isAuthenticated;

  return (
    <>
      <Modal
        show={show && step === "email" && !verifyMode}
        onHide={handleClose}
        centered
        className="add-spot-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add your spot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Lets start with your Email. Your email will not be shared with
            parkers or third-party vendors.
          </p>
          <Form.Group>
            <Form.Label className="fw-bold">Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="you@example.com"
              value={lead.email}
              autoFocus
              disabled={checking}
              onChange={(e) =>
                setLead((l) => ({ ...l, email: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleEmailNext();
                }
              }}
              isInvalid={!!leadErrors.email}
            />
            {leadErrors.email && (
              <Form.Control.Feedback type="invalid">
                {leadErrors.email}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="d-block">
          <div className="d-grid">
            <Button
              type="button"
              variant=""
              className="new-vehicle-submit"
              disabled={checking}
              onClick={handleEmailNext}
            >
              <span>
                {checking ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2" />
                    Checking…
                  </>
                ) : (
                  "Next"
                )}
              </span>
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <AddSpotModal
        show={show && step === "spot" && !verifyMode}
        spot={initialSpot}
        wizardMode
        detailsHeader={authedMode ? null : detailsHeader}
        validateDetailsExtra={authedMode ? null : validateLead}
        onDetailsSave={handleDetailsSave}
        onActivate={handleActivate}
        onStepAdvance={handleStepAdvance}
        onClose={handleClose}
      />

      {verifyMode === "step1-existing" && (
        <ClaimVerifyModal
          show
          email={verifyEmail}
          verifyFn={(payload) => API.verifyOtpLead(payload)}
          resendFn={(payload) => API.leadCheck(payload)}
          onVerified={handleStep1Verified}
        />
      )}

      {verifyMode === "step3-postsignup" && (
        <ClaimVerifyModal
          show
          email={verifyEmail}
          onVerified={handleStep3Verified}
        />
      )}
    </>
  );
};

export default ClaimSpotModal;
